import InputMask from "react-input-mask";
import {
  ChangeEvent,
  useState,
  FormEvent,
  useContext,
  Fragment,
  useEffect,
} from "react";
import RemoveFileIcon from "../../assets/icons/RemoveFileIcon";
import { GlobalContext } from "../../context/globalContext";
import { ButtonPrimary } from "@telefonica/mistica";
import { api, setAuthUID } from "../../lib/api";
import AgilizePaperIcon from "../../assets/icons/AgilizePaperIcon";
import SuccessMessage from "../SuccessMessage";
import * as S from "./styles";
import handleResponsiveScroll from "../../mocks/handleResponsiveScroll";
import CryptoJS from "crypto-js";
import {
  BR_CELLPHONE_DEFAULT_MASKED_VALUE,
  BR_CELLPHONE_INPUT_MASK,
  digitsFromBrazilMaskedPhone,
  isBrazilCellphoneMaskComplete,
} from "../../utils/brazilCellPhoneMask";

interface IFormErrors {
  fullName: string;
  cellphone: string;
  email: string;
  message: string;
  contactForms: string;
  terms: string;
  file: string;
}

const resultFormContactOptions = ["E-mail", "Telefone", "WhatsApp"];

const defaultFormFields = {
  fullName: "",
  cellphone: BR_CELLPHONE_DEFAULT_MASKED_VALUE,
  email: "",
  message: "",
  terms: true as boolean,
  contactForms: resultFormContactOptions,
};

const SimulationResult = () => {
  const {
    resultInformations,
    setSelectedCity,
    setMonthlyValue,
    setSelectedState,
    setShowSimulationResult,
    setResultInformations,
    setPhoneNumber,
  } = useContext(GlobalContext);

  const [formFields, setFormFields] = useState(defaultFormFields);

  const [lightInvoiceFile, setLightInvoiceFile] = useState<File | null>();
  const [apiError, setApiError] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const [loadingFormApi, setLoadingFormApi] = useState(false);
  const [formErrors, setFormErrors] = useState({} as IFormErrors);

  const disableSendButton = loadingFormApi;

  useEffect(() => {
    handleResponsiveScroll(".simulationContainer", { desk: -560, mob: -80 });
  }, []);

  function handleGetInvoiceFile(e: ChangeEvent<HTMLInputElement>) {
    const target = e.target as HTMLInputElement;
    const files = target.files;

    if (files) {
      setLightInvoiceFile(files[0]);
    }
  }

  function handleChangeInputFieldValue(field: string, value: string) {
    setFormFields((oldValue) => {
      return {
        ...oldValue,
        [field]: value,
      };
    });
  }

  function handleWithCheckboxFields(value: string) {
    let formCheckbox: string[] = [];

    const checkIfValueAlreadyExists = formFields.contactForms.some(
      (check) => check === value
    );

    if (checkIfValueAlreadyExists) {
      formCheckbox = formFields.contactForms.filter(
        (values) => values !== value
      );
    } else {
      formCheckbox = [...formFields.contactForms, value];
    }

    setFormFields((oldValue) => {
      return {
        ...oldValue,
        contactForms: formCheckbox,
      };
    });
  }

  function checkErrorsOnForm() {
    const errors = {} as IFormErrors;

    const { fullName, email, cellphone, contactForms, terms } = formFields;

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!fullName) {
      errors.fullName = "Nome obrigatório";
    }

    if (!email) {
      errors.email = "E-mail obrigatório";
    } else if (!email.match(emailRegex)) {
      errors.email = "E-mail inválido";
    }

    if (!isBrazilCellphoneMaskComplete(cellphone)) {
      const d = digitsFromBrazilMaskedPhone(cellphone);
      errors.cellphone =
        d.length <= 2 ? "Telefone obrigatório" : "Telefone inválido";
    }

    if (!contactForms.length) {
      errors.contactForms = "Selecione uma das opções";
    }

    if (terms === false) {
      errors.terms = "É necessário aceitar os termos para prosseguir";
    }

    setFormErrors(errors);
  }

  function getUtmParams() {
    if (typeof window === "undefined") {
      return {
        utmSource: "",
        utmMedium: "",
        utmCampaign: "",
        utmTerm: "",
        utmContent: "",
        dgi: "",
      };
    }

    const searchParams = new URLSearchParams(window.location.search);

    return {
      utmSource: searchParams.get("utm_source") ?? "",
      utmMedium: searchParams.get("utm_medium") ?? "",
      utmCampaign: searchParams.get("utm_campaign") ?? "",
      utmTerm: searchParams.get("utm_term") ?? "",
      utmContent: searchParams.get("utm_content") ?? "",
      dgi: searchParams.get("dgi") ?? "",
    };
  }

  function calculateLeadScore(valorFatura: number) {
    if (valorFatura < 5000) {
      return 0.5;
    }
    if (valorFatura < 8000) {
      return 7.5;
    }
    if (valorFatura < 9000) {
      return 8.5;
    }
    if (valorFatura < 10000) {
      return 9.5;
    }
    if (valorFatura < 200000) {
      return 10.5;
    }

    return 10.5;
  }

  async function submitForm() {
    const formatCellphone = digitsFromBrazilMaskedPhone(formFields.cellphone);

    // Preparar array de contato no formato da API (Telefone,E-mail,WhatsApp)
    const contatoArray = formFields.contactForms.map((c) => {
      if (c === "E-mail") return "E-mail";
      if (c === "Telefone") return "Telefone";
      if (c === "WhatsApp") return "WhatsApp";
      return c;
    });
    const contatoString = contatoArray.join(",");

    const { utmSource, utmMedium, utmCampaign, utmTerm, utmContent, dgi } =
      getUtmParams();

    const valorFatura = Number(resultInformations?.monthlyValue || 0);
    const leadScore = calculateLeadScore(valorFatura);

    try {
      // Primeiro, criar o lead
      const leadData = {
        descontoVisualizado: `${resultInformations?.percentage}%`,
        email: formFields.email,
        leadOrigem: "Landing Page",
        mensagem: formFields.message || "",
        municipio: resultInformations?.city || "",
        municipioIbgeId: resultInformations?.cityId.toString(),
        nome: formFields.fullName,
        origem: "Landing Page",
        telefone: formatCellphone,
        termosUso: formFields.terms,
        valorAproximadoFatura: resultInformations?.monthlyValue,
        contato: contatoString,
        score: leadScore,
        utmSource,
        utmMedium,
        utmCampaign,
        utmTerm,
        utmContent,
        dgi,
      };

      const leadResponse = await api.post("/lead", leadData);

      // Se a resposta do lead incluir um UID, armazenar para upload/survey
      if (leadResponse.data?.uid) {
        setAuthUID(leadResponse.data.uid);
      }

      // Se houver arquivo, fazer upload
      if (lightInvoiceFile) {
        try {
          const formData = new FormData();
          formData.append("file", lightInvoiceFile);          
          await api.post("/upload", formData, {
            timeout: 300000, // 5 minutos
          });
        } catch (uploadError) {
          console.error("Erro no upload:", uploadError);
          // Mesmo que o upload falhe, o lead já foi criado
        }
      }

      setShowSuccessMessage(true);
    } catch (e) {
      console.log(e);
      const errorMessage =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Ocorreu um erro ao enviar as informações...";
      setApiError(errorMessage);
    } finally {
      const tipoLead = localStorage.getItem("tipoLead");
      const consumo = +(localStorage.getItem("consumo") || 0);

      const emailCriptografado = CryptoJS.SHA256(formFields.email).toString();
      const telCriptografado = CryptoJS.SHA256(formFields.cellphone).toString();

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "secondForm_conversion",
        tipoLead: tipoLead,
        consumo: consumo,
        email: emailCriptografado,
        tel: telCriptografado,
      });

      setLoadingFormApi(false);
    }
  }

  async function handleSubmitForm(e: FormEvent) {
    e.preventDefault();

    checkErrorsOnForm();

    setLoadingFormApi(true);
  }

  function handleRedoSimulation() {
    setLoadingFormApi(false);
    setShowSuccessMessage(false);
    setShowSimulationResult(false);
    setResultInformations(null);
    setSelectedCity(null);
    setLightInvoiceFile(null);
    setFormFields(defaultFormFields);
    setMonthlyValue("");
    setSelectedState("");

    setTimeout(() => {
      handleResponsiveScroll(".economySimulation", { desk: -590, mob: -340 });
    }, 300);
  }

  useEffect(() => {
    const doesFormHasErrors = Object.keys(formErrors);

    if (!doesFormHasErrors.length && loadingFormApi) {
      submitForm();
    } else {
      setLoadingFormApi(false);
    }
  }, [formErrors]);

  return (
    <Fragment>
      {!showSuccessMessage ? (
        <S.SimulationRoot>
          <S.SimulationWrapper className="simulationContainer">
            <S.InfoCol>
              <S.MainHeader>
                Sua empresa pode reduzir custos, ganhar previsibilidade e
                fortalecer o fluxo de caixa.
              </S.MainHeader>

              <S.DiscountText>
                Quer se livrar das bandeiras tarifárias e evitar cobranças mais
                altas nos horários de pico? <br />
                Com a GUD, você migra para o Mercado Livre de Energia sem
                alterar seu padrão de consumo e sem precisar investir.
              </S.DiscountText>

              <S.RedoSimulation $hideOnMobile={true}>
                <button onClick={() => handleRedoSimulation()} type="button">
                  Simular novamente
                </button>
              </S.RedoSimulation>
            </S.InfoCol>

            <S.SimulationCol>
              <S.SimulationFormWrapper>
                <S.FormTitle>
                  <strong>Deixe seus dados que entraremos em contato:</strong>
                </S.FormTitle>
                <S.SimulationResultForm onSubmit={handleSubmitForm}>
                  <S.InputSection>
                    <label>
                      <input
                        onChange={({ target }) =>
                          handleChangeInputFieldValue("fullName", target.value)
                        }
                        type="text"
                      />
                      <S.FloatingText
                        $willFloat={Boolean(formFields.fullName)}
                        className={formErrors.fullName ? "errorMsg" : ""}
                      >
                        Nome e sobrenome
                      </S.FloatingText>
                      {formErrors.fullName && (
                        <S.FieldErrorMsg>{formErrors.fullName}</S.FieldErrorMsg>
                      )}
                    </label>
                    <label>
                      <InputMask
                        value={formFields.cellphone}
                        onChange={({ target }) => {
                          handleChangeInputFieldValue(
                            "cellphone",
                            target.value
                          );
                          setPhoneNumber(target.value);
                        }}
                        type="text"
                        mask={BR_CELLPHONE_INPUT_MASK}
                        alwaysShowMask
                      />
                      <S.FloatingText
                        $willFloat={
                          digitsFromBrazilMaskedPhone(formFields.cellphone)
                            .length > 2
                        }
                        className={formErrors.cellphone ? "errorMsg" : ""}
                      >
                        Telefone de contato
                      </S.FloatingText>
                      {formErrors.cellphone && (
                        <S.FieldErrorMsg>
                          {formErrors.cellphone}
                        </S.FieldErrorMsg>
                      )}
                    </label>
                  </S.InputSection>

                  <S.InputSection>
                    <label>
                      <input
                        onChange={({ target }) =>
                          handleChangeInputFieldValue("email", target.value)
                        }
                        type="text"
                      />
                      <S.FloatingText
                        $willFloat={Boolean(formFields.email)}
                        className={formErrors.email ? "errorMsg" : ""}
                      >
                        E-mail de contato
                      </S.FloatingText>
                      {formErrors.email && (
                        <S.FieldErrorMsg>{formErrors.email}</S.FieldErrorMsg>
                      )}
                    </label>
                  </S.InputSection>

                  <S.InputSection>
                    <label>
                      <input
                        onChange={({ target }) =>
                          handleChangeInputFieldValue("message", target.value)
                        }
                        type="text"
                      />
                      <S.FloatingText
                        $willFloat={Boolean(formFields.message)}
                        className={formErrors.message ? "errorMsg" : ""}
                      >
                        Mensagem (opcional)
                      </S.FloatingText>
                      {formErrors.message && (
                        <S.FieldErrorMsg>{formErrors.message}</S.FieldErrorMsg>
                      )}
                    </label>
                  </S.InputSection>

                  <S.CheckboxGroupWrapper>
                    <S.CheckboxSection>
                      <S.CheckboxGroupTitle>
                        Como podemos falar com você?
                      </S.CheckboxGroupTitle>

                      <S.CheckboxArea>
                        {resultFormContactOptions.map((checkbox) => {
                          return (
                            <label
                              key={checkbox}
                              className="control control_checkbox"
                            >
                              <input
                                onChange={({ target }) =>
                                  handleWithCheckboxFields(target.value)
                                }
                                defaultChecked={formFields.contactForms.includes(
                                  checkbox
                                )}
                                value={checkbox}
                                type="checkbox"
                              />
                              <div className="control_indicator" />
                              {checkbox}
                            </label>
                          );
                        })}
                      </S.CheckboxArea>
                    </S.CheckboxSection>
                    <S.FieldErrorMsg>{formErrors.contactForms}</S.FieldErrorMsg>
                  </S.CheckboxGroupWrapper>

                  <S.AgilizeWrapper>
                    <S.AgilizeTitle>
                      <p className="highlighted">
                        Quer agilizar seu atendimento?
                      </p>
                      <p>Anexe uma conta de luz recente (últimos 3 meses)</p>
                    </S.AgilizeTitle>

                    <S.SendInvoice>
                      <AgilizePaperIcon />

                      <S.InvoiceFileInputWrapper>
                        <span>
                          Conta de Luz:
                          <label
                            style={
                              lightInvoiceFile
                                ? { textDecoration: "none" }
                                : { textDecoration: "underline" }
                            }
                          >
                            {lightInvoiceFile
                              ? lightInvoiceFile.name
                              : "Selecionar arquivo"}
                            <input
                              type="file"
                              disabled={!!lightInvoiceFile}
                              onChange={(e) => handleGetInvoiceFile(e)}
                              accept=".pdf, image/*"
                              onClick={(e) => {
                                const target = e.target as HTMLInputElement;

                                target.value = "";
                              }}
                            />
                          </label>
                        </span>
                        {lightInvoiceFile && lightInvoiceFile.name && (
                          <button
                            onClick={() => {
                              setLightInvoiceFile(null);
                            }}
                            type="button"
                          >
                            <RemoveFileIcon />
                          </button>
                        )}
                      </S.InvoiceFileInputWrapper>
                    </S.SendInvoice>
                    {apiError && <S.FileError>{apiError}</S.FileError>}
                    <S.FileError>
                      {formErrors.file && formErrors.file}
                    </S.FileError>
                  </S.AgilizeWrapper>
                  <S.InvoiceFormFooter>
                    <S.CheckboxArea>
                      <label className="control control_checkbox">
                        <input
                          defaultChecked={true}
                          type="checkbox"
                          onChange={({ target }) =>
                            // @ts-ignore
                            handleChangeInputFieldValue("terms", target.checked)
                          }
                          // @ts-ignore
                          value={formFields.terms}
                        />
                        <p>
                          Ao enviar você autoriza a GUD e seus parceiros a
                          entrar em contato com você via e-mail, telefone ou
                          WhatsApp e declara ter lido a{" "}
                          <a target="__blank" href="/normas-de-privacidade">
                            Norma de Privacidade
                          </a>
                          .
                        </p>
                        <div className="control_indicator" />
                      </label>
                    </S.CheckboxArea>
                  </S.InvoiceFormFooter>
                  {formErrors.terms && (
                    <S.FieldErrorMsg>{formErrors.terms}</S.FieldErrorMsg>
                  )}
                  <S.SendInvoiceButton
                    id="btnEnviar"
                    $formLoading={loadingFormApi}
                  >
                    <ButtonPrimary
                      className={`simulateButton ${
                        lightInvoiceFile
                          ? "secondForm_conversion_with_invoice"
                          : "secondForm_conversion"
                      }`}
                      loadingText="Enviando..."
                      submit
                      disabled={disableSendButton}
                      showSpinner={loadingFormApi}
                    >
                      {lightInvoiceFile ? "Enviar" : "Enviar sem fatura"}
                    </ButtonPrimary>
                  </S.SendInvoiceButton>
                </S.SimulationResultForm>
              </S.SimulationFormWrapper>

              <S.RedoSimulation $hideOnMobile={false}>
                <button onClick={() => handleRedoSimulation()} type="button">
                  Simular novamente
                </button>
              </S.RedoSimulation>
            </S.SimulationCol>
          </S.SimulationWrapper>
        </S.SimulationRoot>
      ) : (
        <SuccessMessage ineligible={false} />
      )}

      {/*  <ResultSmsMessage /> */}
    </Fragment>
  );
};

export default SimulationResult;
